import store from '@/store';
import ProductHelper from '@/helpers/productHelper';
import PaymentTypes from '@/models/PaymentTypes';

/**
 * Regras relacionadas a view de installments
 */
export class CreditCardInstallmentsViewRules {
    /**
     * Regra para exibir ou não a view de installments
     */
    static ShouldShow() {
        let showInstallmentsView = store.getters['recurrentPayment/isRecurringPayment'] == false;

        return showInstallmentsView;
    }
}

export class CreditCardViewRules {
    /**
     * Regra genérica para exibir ou não as views referentes a cartão de credito
     */
    static ShouldShow() {
        const isCreditCardPayment = store.state.payment.PaymentType == PaymentTypes.CREDIT_CARD;
        return isCreditCardPayment;
    }
}

/**
 * Rules related to the ChangePaymentType view.
 *
 * Should not render change payment type view when the payment
 * is recurring because all the recurring payments are credit card
 * based, so there is no need to the user to see this page.
 *
 * @see MainPage.vue
 * @export
 * @returns {bool} Bool that defines if the view is going to be rendered.
 */
export function shouldRenderPaymentTypeView() {
    const isRecurringPayment = store.getters['recurrentPayment/isRecurringPayment'];
    const hasDofData = store.getters['dataOnFile/hasDofData'];
    
    if (isRecurringPayment && hasDofData) {
        return isRecurringPayment;
    }

    return !isRecurringPayment;
}

/**
 * Rules related to all the shipping views.
 *
 * Should not render any shipping view when the payment is recurring.
 *
 * @export
 * @returns {bool} Bool that defines if the view is going to be rendered.
 */
export function shouldRenderShippingView() {

    return ProductHelper.productHasShipping();
}

export function shouldRenderIdentificationView() {
    const isDofEnabled = store.getters['dataOnFile/isDofEnabled'];
    const isAuthenticated = store.getters['dataOnFile/isAuthenticated'];
    return Boolean(isDofEnabled) && !isAuthenticated;
}

export function shouldRenderCheckOtpView() {
    const isDofEnabled = store.getters['dataOnFile/isDofEnabled'];
    const skipOtpView = store.getters['dataOnFile/getSkipOtpView'];
    const isAuthenticated = store.getters['dataOnFile/isAuthenticated'];
    return Boolean(isDofEnabled) && !skipOtpView && !isAuthenticated;
}

function isReviewTargetPaymentType(paymentType) {
    const reviewTargetPaymentTypes = [
        PaymentTypes.CREDIT_CARD,
        PaymentTypes.DEBIT_CARD,
        PaymentTypes.BOLETO,
        PaymentTypes.QRCODE,
        PaymentTypes.QRCODE_DEBIT,
        PaymentTypes.PIX,
        PaymentTypes.PIX_OPEN_FINANCE,
        PaymentTypes.VOUCHER,
        PaymentTypes.MULTIBENEFITS,
    ];

    return reviewTargetPaymentTypes.includes(paymentType);
}

function shouldEnableNonDofReview() {
    const isAuthenticated = store.getters['dataOnFile/isAuthenticated'];
    const isDofGuest = store.getters['dataOnFile/isDofGuest'];

    return Boolean(isAuthenticated) && !isDofGuest;
}

function hasPendingCustomerData() {
    const customer = store.state.customer || {};

    return !customer.Name || !customer.Email || !customer.Phone || !customer.Identity;
}

function hasPendingBoletoBillingData() {
    const paymentType = store.state.payment.PaymentType;
    if (paymentType !== PaymentTypes.BOLETO) return false;

    const billingAddress = store.state.billingAddress || {};

    if (billingAddress.Type === 'national') {
        return (
            !billingAddress.Street ||
            !billingAddress.State ||
            !billingAddress.City ||
            !billingAddress.Number ||
            !billingAddress.District ||
            !billingAddress.ZipCode
        );
    }

    return (
        !billingAddress.Street ||
        !billingAddress.State ||
        !billingAddress.City ||
        !billingAddress.ZipCode
    );
}

export function hasPendingPersonalDataForReview() {
    return hasPendingCustomerData() || hasPendingBoletoBillingData();
}

/**
 * Regra específica do fluxo Data on File (cartão fechado).
 *
 * Por conta de uma inconsistência na API do DoF, é possível existir um
 * customer salvo sem identity/identityType preenchidos. Essa regra
 * detecta essa pendência para que o front redirecione o usuário para
 * a tela de edição de dados (/dof/edit-customer) antes da review.
 *
 * @export
 * @returns {bool} true se houver dado de customer pendente no DoF.
 */
export function hasPendingDofCustomerData() {
    const customer = store.getters['dataOnFile/getCustomer'] || {};

    return !customer.fullName || !customer.email || !customer.phone || !customer.identity || !customer.identityType;
}

export function shouldRenderReviewView() {
    const paymentType = store.state.payment.PaymentType;

    return isReviewTargetPaymentType(paymentType) && shouldEnableNonDofReview();
}

export function shouldRenderPersonalDataView() {
    if (!shouldRenderReviewView()) return true;

    return hasPendingPersonalDataForReview();
}
